/* nplus-attribution.js: ad and affiliate attribution for Stripe card payments.
   Classic script, loaded once from layout/theme.liquid. Exposes window.nplusAttribution.
   Ported from nplus-eu (same consent rules and storage), plus the AvantLink click ID.

   Why: a card payment on the PDP is paid on a Stripe-hosted page, not Shopify checkout,
   so the AvantLink app pixel never sees the sale and the affiliate is never credited.
   This file hands the backend (nplus-deposit-app /create-checkout-session) the AvantLink
   click ID and the ad source with the payment request; the backend puts them on the
   Shopify order (attribution_* attributes, tag affiliate-avantlink).

   Consent: nothing is read or stored until the visitor allows marketing (same Shopify
   Customer Privacy check as snippets/gtm-head.liquid and snippets/nplus-consent-loader.liquid).
   If marketing consent is later withdrawn, get() returns null and the stored copy is removed.

   What it keeps (localStorage key nplus_attribution, last touch wins, 90 days):
     utm_source, utm_medium, utm_campaign, utm_term, utm_content, gclid, gbraid, wbraid,
     fbclid, avad, landing_path (path only, never the query string), captured_at (ISO).
   A visit with no UTM or click ID in the URL never overwrites an earlier touch.
   avad also honours AvantLink's own expiry (avad_ttl, unix seconds, in the same URL):
   once that passes, get() leaves avad out even if the rest is still within 90 days.

   get() also reads the click IDs the Google and Meta tags already store first-party:
     _gcl_aw cookie "GCL.<ts>.<gclid>"  -> gclid (preferred over the URL copy)
     _fbc cookie   "fb.1.<ts>.<fbclid>" -> fbc   (built from a stored fbclid if the cookie is missing)
*/
(function () {
  'use strict';

  var KEY = 'nplus_attribution';
  var MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;
  var URL_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'gbraid', 'wbraid', 'fbclid', 'avad'];
  // AvantLink click ID, e.g. "275977_b4fd474a1". Same shape the backend accepts.
  var AVAD_RE = /^[A-Za-z0-9_-]{1,64}$/;
  var MAX_AVAD_TTL_S = 365 * 24 * 60 * 60;
  var MAX_LEN = 255;

  function marketingAllowed() {
    var cp = window.Shopify && window.Shopify.customerPrivacy;
    if (!cp) return false;
    if (typeof cp.marketingAllowed === 'function') return !!cp.marketingAllowed();
    var current = typeof cp.currentVisitorConsent === 'function' ? (cp.currentVisitorConsent() || {}) : {};
    return current.marketing === 'yes';
  }

  // Email platforms sometimes put the subscriber's address in utm_term / utm_content:
  // any value containing an email address is dropped so no PII is kept or sent.
  var EMAIL_LIKE_RE = /[^\s@]+@[^\s@]+\.[^\s@]+/;
  var FBC_RE = /^fb\.\d+\.\d+\.\S+$/;

  function clean(v) {
    if (v == null) return '';
    var s = String(v).trim().slice(0, MAX_LEN);
    var decoded = s;
    try { decoded = decodeURIComponent(s); } catch (e) { /* keep raw */ }
    return EMAIL_LIKE_RE.test(decoded) ? '' : s;
  }

  function readStore() {
    try {
      var raw = window.localStorage.getItem(KEY);
      if (!raw) return null;
      var data = JSON.parse(raw);
      var at = Date.parse(data && data.captured_at);
      if (!at || Date.now() - at > MAX_AGE_MS) { window.localStorage.removeItem(KEY); return null; }
      return data;
    } catch (e) { return null; }
  }

  function writeStore(data) {
    try { window.localStorage.setItem(KEY, JSON.stringify(data)); } catch (e) { /* storage blocked: nothing to do */ }
  }

  function clearStore() {
    try { window.localStorage.removeItem(KEY); } catch (e) { /* ignore */ }
  }

  function cookie(name) {
    var match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
    if (!match) return '';
    try { return decodeURIComponent(match[1]); } catch (e) { return match[1]; }
  }

  // Save the current URL's UTMs and click IDs, if any. Only with marketing consent.
  function capture() {
    if (!marketingAllowed()) { clearStore(); return; }
    var params;
    try { params = new URLSearchParams(window.location.search); } catch (e) { return; }
    var found = {};
    var any = false;
    URL_PARAMS.forEach(function (name) {
      var v = clean(params.get(name));
      if (v) { found[name] = v; any = true; }
    });
    if (found.avad && !AVAD_RE.test(found.avad)) {
      delete found.avad;
      any = Object.keys(found).length > 0;
    }
    if (!any) return;
    if (found.avad) {
      // AvantLink's expiry for this click, if it sent a sane one.
      var ttl = parseInt(params.get('avad_ttl'), 10);
      var nowS = Math.floor(Date.now() / 1000);
      if (ttl > nowS && ttl - nowS <= MAX_AVAD_TTL_S) found.avad_expires = ttl;
    }
    found.landing_path = clean(window.location.pathname);
    found.captured_at = new Date().toISOString();
    writeStore(found);
  }

  // Attribution for an enquiry payload, or null when there is none or no consent.
  function get() {
    if (!marketingAllowed()) { clearStore(); return null; }
    var stored = readStore() || {};
    var out = {};
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gbraid', 'wbraid', 'landing_path', 'captured_at'].forEach(function (k) {
      if (stored[k]) out[k] = clean(stored[k]);
    });

    if (stored.avad && AVAD_RE.test(stored.avad)) {
      var exp = parseInt(stored.avad_expires, 10);
      if (!exp || exp > Math.floor(Date.now() / 1000)) out.avad = stored.avad;
    }

    var gclAw = cookie('_gcl_aw');
    var gclid = gclAw && /^GCL\.\d+\./.test(gclAw) ? gclAw.split('.').slice(2).join('.') : '';
    gclid = clean(gclid || stored.gclid);
    if (gclid) out.gclid = gclid;

    var fbc = clean(cookie('_fbc'));
    if (fbc && !FBC_RE.test(fbc)) fbc = '';
    if (!fbc && stored.fbclid) fbc = clean('fb.1.' + (Date.parse(stored.captured_at) || Date.now()) + '.' + stored.fbclid);
    if (fbc) out.fbc = fbc;

    return Object.keys(out).length ? out : null;
  }

  function init() {
    if (window.Shopify && window.Shopify.customerPrivacy) { capture(); return; }
    if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (error) {
        if (!error) capture();
      });
    }
  }

  window.nplusAttribution = { get: get, capture: capture };
  document.addEventListener('visitorConsentCollected', capture);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
